import {Product} from '@hconnect/apiclient'
import {FormToggle} from '@hconnect/uikit/src/lib2'
import {Box, Divider, TextField, Typography} from '@mui/material'
import React, {useEffect} from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useGlobalState} from '../../../../hooks/useGlobalState'
import {useAvailablePermissions} from '../../hooks/useAvailablePermissions'
import {RequestAccessSelfOnboardingFormData} from '../../RequestAccessSelfOnboarding.types'
import {useRafStyles} from '../../styles'

import {useStyles} from './AccessLevelStep.styles'
import {AdditionalDetails} from './AdditionalDetails'
import {HubAccessInput} from './inputs/HubAccessInput'
import {OnsiteAccessInput} from './inputs/OnsiteAccessInput'

const MAX_EMAIL_CHARACTERS_LENGTH = 2000

export const AccessLevelStep = () => {
  const formMethods = useFormContext<RequestAccessSelfOnboardingFormData>()
  const {t} = useTranslation()
  const {classes: generalClasses} = useRafStyles()
  const {classes} = useStyles()
  const {globalState} = useGlobalState()
  const countryId = formMethods.watch('countryId')
  const permissions = formMethods.watch('permissions')

  const {availablePermissions} = useAvailablePermissions(countryId)
  const currentPermissions = formMethods.watch('permissions')
  const isRiskyRoleSelected =
    currentPermissions?.['canCreateOrders'] || currentPermissions?.['canChangeOrders']
  const isViewInvoicesToggled = currentPermissions?.['canSeeInvoices']

  const wordsLength = formMethods.watch('comment')?.length || 0

  useEffect(() => {
    if (isViewInvoicesToggled) {
      formMethods.resetField('deliveryTicketNr')
      formMethods.resetField('deliveryTicketDate')
      formMethods.resetField('permissions.canCreateAndChangeOrders')
      formMethods.resetField('permissions.canSeeTestsAndCertificates')
    } else {
      formMethods.resetField('invoiceNr')
      formMethods.resetField('invoiceNetTotal')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewInvoicesToggled])

  useEffect(() => {
    if (availablePermissions && !permissions) {
      formMethods.reset({
        ...formMethods.getValues(),
        permissions: availablePermissions
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePermissions])

  useEffect(() => {
    if (currentPermissions?.['canCreateOrders']) {
      formMethods.setValue('permissions.canChangeOrders', true)
      formMethods.setValue('permissions.canSeeTestsAndCertificates', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPermissions, formMethods])

  const handleDisableToggles = (permissionKey: string) => {
    switch (permissionKey) {
      case 'canSeeOrderAndDeliveries':
        return true
      case 'canSeeInvoices':
        return isRiskyRoleSelected
      case 'canChangeOrders': {
        return isViewInvoicesToggled || currentPermissions?.['canCreateOrders']
      }
      case 'canCreateOrders':
        return isViewInvoicesToggled
      case 'canSeeTestsAndCertificates':
        return isViewInvoicesToggled || currentPermissions?.['canCreateOrders']
      default:
        return false
    }
  }

  return (
    <Box style={{display: 'flex', flexDirection: 'column', width: '100%', marginBottom: '12px'}}>
      <Typography className={generalClasses.title} style={{marginBottom: '32px'}}>
        {t('authenticator.requestAccessSelfOnboarding.accessLevelStep.title')}
      </Typography>
      <Typography className={generalClasses.subTitle} style={{marginBottom: '16px'}}>
        {t('authenticator.requestAccessSelfOnboarding.accessLevelStep.permissionsTitle')}
      </Typography>
      {availablePermissions &&
        Object.keys(availablePermissions)?.map((permissionKey) => (
          <Controller
            key={permissionKey}
            name={`permissions.${permissionKey}` as keyof RequestAccessSelfOnboardingFormData}
            control={formMethods.control}
            render={({field}) => (
              <FormToggle
                toggleKey={permissionKey}
                label={t(
                  `authenticator.requestAccessSelfOnboarding.accessLevelStep.permissionToggles.${permissionKey}`
                )}
                disabled={handleDisableToggles(permissionKey)}
                data-test-id={`access-level-toggle-${permissionKey}`}
                checked={
                  !!availablePermissions?.[permissionKey] || !!currentPermissions?.[permissionKey]
                }
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />
        ))}
      <Controller
        name="comment"
        control={formMethods.control}
        render={({field}) => (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: '32px'
            }}
          >
            <TextField
              {...field}
              multiline={true}
              minRows={4}
              className={classes.multilineText}
              type="text"
              variant="standard"
              InputProps={{
                disableUnderline: true
              }}
              InputLabelProps={{shrink: true}}
              label={t('authenticator.requestAccessSelfOnboarding.accessLevelStep.commentLabel')}
              placeholder={t(
                'authenticator.requestAccessSelfOnboarding.accessLevelStep.commentDescription'
              )}
              data-test-id="access-level-comment"
            />
            <span
              style={{
                placeSelf: 'flex-end',
                fontSize: '12px',
                color: formMethods.formState.errors?.comment?.message && 'rgba(218, 9, 1, 1)'
              }}
            >{`${
              wordsLength > MAX_EMAIL_CHARACTERS_LENGTH
                ? t(`authenticator.errorMessages.${formMethods.formState.errors?.comment?.message}`)
                : ''
            } ${wordsLength}/${MAX_EMAIL_CHARACTERS_LENGTH}`}</span>
          </Box>
        )}
      />
      {/* TODO: Uncomment once proper implementation of access to projects is ready*/}
      {/* <Typography className={generalClasses.subTitle} style={{marginBottom: '8px'}}>*/}
      {/*  {t('authenticator.requestAccessSelfOnboarding.accessLevelStep.accessAllTitle')}*/}
      {/* </Typography>*/}
      {/* <Typography className={generalClasses.description} style={{marginBottom: '16px'}}>*/}
      {/*  {t('authenticator.requestAccessSelfOnboarding.accessLevelStep.accessAllDescription')}*/}
      {/* </Typography>*/}
      {/* <Controller*/}
      {/*  name="allProjectsAccess"*/}
      {/*  control={formMethods.control}*/}
      {/*  render={({field}) => (*/}
      {/*    <FormToggle*/}
      {/*      toggleKey="all-projects-access"*/}
      {/*      label={t('authenticator.requestAccessSelfOnboarding.accessLevelStep.accessAllLabel')}*/}
      {/*      data-test-id={'access-to-all-projects-toggle'}*/}
      {/*      checked={field.value as boolean}*/}
      {/*      onChange={field.onChange}*/}
      {/*    />*/}
      {/*  )}*/}
      {/*/ >*/}
      {!isRiskyRoleSelected && (
        <>
          <AdditionalDetails />
          <Divider style={{borderColor: '#D1D9E0', marginBottom: '24px'}} />
        </>
      )}
      {globalState.clientId !== Product.OnSite ? <OnsiteAccessInput /> : <HubAccessInput />}
    </Box>
  )
}
